import { useAuth } from "../../context/AuthContext";
import { useEffect, useState } from "react";
import ClientSidebarLinks from "./ClientSidebarLinks";

type CardProps = {
  title: string;
  value: string;
};

type TeamMemberProps = {
  name: string;
  role: string;
};
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<any[]>([]);
  const [team, setTeam] = useState<TeamMemberProps[]>([]);

  useEffect(() => {
    if (user?.id) {
      fetch(
        `${API_BASE}/get_user_projects.php?user_id=${user.id}`
      )
        .then((res) => res.json())
        .then((data) => setProjects(data))
        .catch((err) => console.error(err));

      fetch(
        `${API_BASE}/get_project_team.php?user_id=${user.id}`
      )
        .then((res) => res.json())
        .then((data) => setTeam(data))
        .catch((err) => console.error(err));
    }
  }, [user]);

  return (
    <div className="flex-1 flex bg-gray-100">
      {/* Sidebar */}
      <aside className="w-64 bg-white shadow-md p-5">
        <h2 className="text-2xl font-bold text-green-600 mb-6">
          {user?.name || "Client"}
        </h2>
        <nav className="space-y-2">
          <ClientSidebarLinks active="myproject" />
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6">
        <div className="mb-3">
          <span className="text-xs font-bold uppercase tracking-wider text-green-600 bg-green-50 border border-green-200 px-3 py-1 rounded-full">
            Client Project Portal & Dashboard
          </span>
        </div>
        <h1 className="text-2xl font-semibold mb-1">
          Welcome, {user?.name}
        </h1>
        <p className="text-gray-500 mb-6">
          Here’s the status of your assigned projects
        </p>

        {/* 🔥 Dynamic Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card
            title="Total Projects"
            value={projects.length.toString()}
          />
          <Card
            title="Completed"
            value={projects
              .filter((p) => p.status === "completed")
              .length.toString()}
          />
          <Card
            title="In Progress"
            value={projects
              .filter((p) => p.status === "in-progress")
              .length.toString()}
          />
          <Card
            title="Pending"
            value={projects
              .filter((p) => p.status === "pending")
              .length.toString()}
          />
        </div>

        {/* 🔥 Assigned Projects Section */}
        <div className="bg-white rounded-xl p-5 shadow mb-6">
          <h3 className="font-semibold mb-4">
            Your Assigned Projects
          </h3>

          {projects.length === 0 ? (
            <p className="text-gray-500">
              No project assigned yet.
            </p>
          ) : (
            projects.map((project) => (
              <div
                key={project.id}
                className="py-3 border-b last:border-b-0"
              >
                <p className="font-medium">{project.name}</p>
                <p className="text-sm text-gray-500">
                  Status: {project.status}
                </p>
              </div>
            ))
          )}
        </div>

        {/* Progress */}
        <div className="bg-white rounded-xl p-5 shadow mb-6">
          <h3 className="font-semibold mb-3">
            Project Progress
          </h3>
          <div className="w-full h-3 bg-gray-200 rounded-full">
            <div
              className="h-3 bg-green-600 rounded-full"
              style={{
                width:
                  projects.length > 0
                    ? `${(
                      (projects.filter(
                        (p) => p.status === "completed"
                      ).length /
                        projects.length) *
                      100
                    ).toFixed(0)}%`
                    : "0%",
              }}
            ></div>
          </div>
        </div>

        {/* Team */}
        <div className="bg-white rounded-xl p-5 shadow">
          <h3 className="font-semibold mb-4">
            Your Project Team
          </h3>
          {team.length === 0 ? (
            <p className="text-gray-500">No team members assigned.</p>
          ) : (
            team.map((member, index) => (
              <TeamMember
                key={index}
                name={member.name}
                role={member.role}
              />
            ))
          )}
        </div>
      </main>
    </div>
  );
}

function Card({ title, value }: CardProps) {
  return (
    <div className="bg-white rounded-xl p-5 shadow">
      <p className="text-gray-500 text-sm">{title}</p>
      <h2 className="text-2xl font-bold mt-2">{value}</h2>
    </div>
  );
}

function TeamMember({ name, role }: TeamMemberProps) {
  return (
    <div className="flex justify-between items-center py-3 border-b last:border-b-0">
      <div>
        <p className="font-medium">{name}</p>
        <p className="text-sm text-gray-500">{role}</p>
      </div>
    </div>
  );
}
